import { useColorMode } from '@chakra-ui/react';
import { useEffect } from 'react';

type SuiteMode = 'light' | 'dark' | 'system';

/**
 * Resolves the suite runtime's stored mode ("system" included) to the
 * concrete light/dark value Chakra's color mode expects.
 */
function resolveMode(mode: SuiteMode): 'light' | 'dark' {
    if (mode === 'system') {
        return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return mode;
}

/**
 * Bridges the suite's shared theme runtime (window.SuiteTheme — the owner of
 * the appearance mode and its storage) onto Chakra UI: Chakra's color mode
 * follows the runtime's initial value and every external change, so Chakra
 * components stay in step with the token sheet the runtime drives. The
 * `data-theme` attribute itself is written by the runtime; this component no
 * longer touches the DOM or storage.
 */
export const ThemeSync = () => {
    const { setColorMode } = useColorMode();

    useEffect(() => {
        const api = window.SuiteTheme;
        if (!api) return;
        setColorMode(resolveMode(api.mode));
        const unsubscribe = api.onChange((state) => {
            setColorMode(resolveMode(state.mode));
        });
        return unsubscribe;
    }, [setColorMode]);

    // Allow commands (e.g. the Keyboard Shortcuts "Toggle Color Theme") to
    // switch the theme through a global event; the flip goes through the
    // suite runtime so storage stays canonical (yap pins its own mode).
    useEffect(() => {
        const handleToggle = () => {
            const api = window.SuiteTheme;
            if (!api) return;
            api.setMode(api.mode === 'dark' ? 'light' : 'dark', true);
        };
        window.addEventListener('yap:toggle-theme', handleToggle);
        return () => window.removeEventListener('yap:toggle-theme', handleToggle);
    }, []);

    return null;
};
